/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Fragment } from "react";
import { Else, If, Then } from "react-if";
import { Link } from "react-router-dom";
import { Box, Button, FlexContainer, Text, theme } from "@nordcloud/gnui";
import { ROUTES } from "~/routing/routes";
import { formatText } from "~/tools";
import {
  convertMinutesToReadableTime,
  isAdvanceTrigger,
} from "~/views/plans/utils";
import {
  PlanNotificationItemType,
  PlanActionNotificationItemType,
} from "../types";

type Props = {
  item: PlanNotificationItemType | PlanActionNotificationItemType;
  index: number;
  onSendTest?: () => void;
  isSendTestLoading?: boolean;
};

export function NotificationItem({
  item,
  index,
  onSendTest,
  isSendTestLoading,
}: Props) {
  const notificationCount = index + 1;

  return (
    <Box
      aria-label={`notification-box`}
      boxStyle="lightGrey"
      mb={theme.spacing.spacing03}
    >
      <FlexContainer
        justifyContent="space-between"
        alignItems="flex-start"
        mb={theme.spacing.spacing04}
      >
        <Text size="sm" tag="div" weight="medium" mb={0}>
          Notification #{notificationCount}
        </Text>
        <If condition={!!onSendTest}>
          <Then>
            <Button
              severity="low"
              data-testid="send-test-button"
              disabled={isSendTestLoading}
              initialState={isSendTestLoading ? "loading" : ""}
              onClick={onSendTest}
            >
              Send Test
            </Button>
          </Then>
        </If>
      </FlexContainer>
      <Text tag="div" align="left" mb={theme.spacing.spacing04}>
        <>
          <Text size="sm" tag="div" color={theme.color.text.text02}>
            Subject:{" "}
          </Text>
          <Text data-testid="notification-subject" tag="div">
            {formatText(item.scope)}
          </Text>
          <Text
            size="sm"
            tag="div"
            color={theme.color.text.text02}
            mt={theme.spacing.spacing04}
          >
            Condition:{" "}
          </Text>
          <If condition={isAdvanceTrigger(item.triggerEvent)}>
            <Then>
              <Text data-testid="notification-condition" tag="span">
                {formatText(item.triggerEvent)} in{" "}
                {convertMinutesToReadableTime(Number(item?.inAdvance))}
              </Text>
            </Then>
            <Else>
              <Text data-testid="notification-condition" tag="span">
                {formatText(item.triggerEvent)}
              </Text>
            </Else>
          </If>
        </>
      </Text>
      <Text
        size="sm"
        tag="div"
        color={theme.color.text.text02}
        data-testid="notification-text"
      >
        Notification Recipients:{" "}
        <Text
          tag="div"
          data-testid="notification-recipients"
          color={theme.color.text.text01}
        >
          {item.notificationGroups.map((group, groupIndex) => (
            <Fragment key={group.id ?? `${group.name}-${groupIndex}`}>
              {groupIndex > 0 && ", "}
              {group.id ? (
                <Link to={ROUTES.notificationGroups.generateDetails(group.id)}>
                  {group.name}
                </Link>
              ) : (
                group.name
              )}
            </Fragment>
          ))}
        </Text>
      </Text>
    </Box>
  );
}
